import { cn } from "../../../utils/cn.js";
import { Spinner } from "../Spinner/Spinner.js";
import { CheckCircleIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import "react";
import { jsx } from "react/jsx-runtime";
import { Toast } from "@base-ui/react/toast";
//#region src/components/Toast/Toast.tsx
/**
* Provides context for creating and managing toasts.
* Wrap the application root so all toasts share a single viewport.
*
* @param props - Provider configuration including an optional global toastManager
*/
function ToastProvider(props) {
	return /* @__PURE__ */ jsx(Toast.Provider, { ...props });
}
/**
* Fixed viewport that contains stacked toast notifications.
*
* Renders inside a portal and positions toasts at the bottom-right of the
* screen. Toasts stack and expand on hover via CSS custom properties
* provided by Base UI.
*
* @param props - Viewport props including optional className overrides
* @returns The rendered viewport container
*/
function ToastViewport({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Portal, { children: /* @__PURE__ */ jsx(Toast.Viewport, {
		className: cn("fixed bottom-lg right-lg z-[100] flex w-[420px]", className),
		...props
	}) });
}
/**
* Curri-styled toast card built on Base UI Toast.Root.
*
* Handles the full stacking behaviour: collapsed toasts peek behind the
* frontmost, scaled down by index. On viewport hover the stack expands
* vertically. Swipe-to-dismiss and enter/exit transitions are included.
*
* @param props - Toast.Root props including the required `toast` object
* @returns The rendered toast card
*/
function ToastRoot({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Root, {
		className: cn("[--gap:0.75rem] [--peek:0.75rem]", "[--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]", "[--height:var(--toast-frontmost-height,var(--toast-height))]", "[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))]", "absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] w-full origin-bottom select-none", "[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))]", "data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))]", "h-[var(--height)] data-[expanded]:h-[var(--toast-height)]", "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']", "[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]", "data-[starting-style]:[transform:translateY(150%)]", "data-[ending-style]:opacity-0 data-[limited]:opacity-0", "[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)]", "data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]", "data-[expanded]:data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]", "data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]", "data-[expanded]:data-[ending-style]:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]", "data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", "data-[expanded]:data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", "data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]", "data-[expanded]:data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]", "box-border font-body min-w-[315px] max-w-[408px] rounded-sm p-md", "border border-(length:--border-width-default) border-border-primary", "bg-elevation-surface-overlay bg-clip-padding shadow-elevation-overlay", className),
		...props
	});
}
/**
* Inner content wrapper that handles stacking overflow and layout.
*
* Arranges icon, body, and close button in a horizontal row with 8 px gap.
* Behind-toast content is hidden when collapsed and fades in on viewport
* hover (expanded).
*
* @param props - Content props including optional className overrides
* @returns The rendered content layout
*/
function ToastContent({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Content, {
		className: cn("flex items-start gap-xs overflow-hidden", "transition-opacity [transition-duration:250ms]", "data-[behind]:pointer-events-none data-[behind]:opacity-0", "data-[expanded]:pointer-events-auto data-[expanded]:opacity-100", className),
		...props
	});
}
/**
* Renders a status icon or loading spinner based on the toast type.
*
* - `"loading"` renders a brand-colored Spinner
* - `"success"` renders a filled CheckCircle (icon-success)
* - `"warning"` / `"error"` renders a filled WarningCircle (icon-warning / icon-danger)
*
* Returns null when the type is unrecognized.
*
* @param props - Icon props including the toast type
* @returns The rendered icon element, or null
*/
function ToastIcon({ type, className, ...props }) {
	const iconClass = "h-4 w-4";
	let icon;
	switch (type) {
		case "loading":
			icon = /* @__PURE__ */ jsx(Spinner, { className: iconClass });
			break;
		case "success":
			icon = /* @__PURE__ */ jsx(CheckCircleIcon, {
				className: cn(iconClass, "text-icon-success"),
				weight: "fill"
			});
			break;
		case "warning":
			icon = /* @__PURE__ */ jsx(WarningCircleIcon, {
				className: cn(iconClass, "text-icon-warning"),
				weight: "fill"
			});
			break;
		case "error":
			icon = /* @__PURE__ */ jsx(WarningCircleIcon, {
				className: cn(iconClass, "text-icon-danger"),
				weight: "fill"
			});
			break;
		default: return null;
	}
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex shrink-0 items-center justify-center", className),
		...props,
		children: icon
	});
}
/**
* Vertical layout container for toast text content and action.
*
* Stacks ToastHeader and ToastAction vertically with a 16 px gap,
* filling available horizontal space via flex-1.
*
* @param props - Standard div props
* @returns The rendered body container
*/
function ToastBody({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex min-w-0 flex-1 flex-col gap-md", className),
		...props
	});
}
/**
* Groups the toast title and description with tight 2 px vertical spacing.
*
* @param props - Standard div props
* @returns The rendered header container
*/
function ToastHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("flex flex-col gap-xxxs", className),
		...props
	});
}
/**
* Renders the toast heading using the Base UI Toast.Title primitive
* for proper accessibility labeling.
*
* Styled with body/medium typography and primary text color.
* Resets browser default heading margin.
*
* @param props - Title props forwarded to Base UI Toast.Title
* @returns The rendered title element
*/
function ToastTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Title, {
		className: cn("m-0 text-body-medium leading-body-medium font-heading-medium text-text-primary", className),
		...props
	});
}
/**
* Renders the toast body text using the Base UI Toast.Description primitive
* for proper accessibility wiring.
*
* Styled with body/medium typography and secondary text color.
* Resets browser default paragraph margin.
*
* @param props - Description props forwarded to Base UI Toast.Description
* @returns The rendered description element
*/
function ToastDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Description, {
		className: cn("m-0 text-body-medium leading-body-medium font-heading-medium text-text-secondary", className),
		...props
	});
}
/**
* An action button styled as an underlined text link.
*
* Matches the Figma toast action treatment: body/medium typography with
* underline decoration and transparent background. Includes vertical
* padding for a comfortable click target.
*
* @param props - Action props forwarded to Base UI Toast.Action
* @returns The rendered action button
*/
function ToastAction({ className, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Action, {
		className: cn("inline-flex cursor-pointer items-center self-start rounded-xs border-0 bg-transparent px-0 py-button-height", "text-body-medium leading-body-medium font-heading-medium text-text-primary underline", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", className),
		...props
	});
}
/**
* Pre-styled close icon button for dismissing the toast.
*
* Renders a Phosphor X icon inside a Base UI Toast.Close primitive with
* hover and focus-visible treatments matching the Dialog close button pattern.
*
* @param props - Close button configuration and optional icon size
* @returns The rendered icon close button
*/
function ToastClose({ className, iconSize = 12, ...props }) {
	return /* @__PURE__ */ jsx(Toast.Close, {
		className: cn("inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xs border-0 bg-background-neutral-secondary p-button-height text-text-secondary outline-none", "hover:bg-background-neutral-secondary-hover hover:text-text-primary", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", className),
		...props,
		children: /* @__PURE__ */ jsx(XIcon, { style: {
			height: iconSize,
			width: iconSize
		} })
	});
}
const createToastManager = Toast.createToastManager;
const useToastManager = Toast.useToastManager;
//#endregion
export { ToastAction, ToastBody, ToastClose, ToastContent, ToastDescription, ToastHeader, ToastIcon, ToastProvider, ToastRoot, ToastTitle, ToastViewport, createToastManager, useToastManager };

//# sourceMappingURL=Toast.js.map